// src/features/tidsregistering/pages/DayOverviewApprovalsPage.jsx
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, CircularProgress,
  Alert, TextField, InputAdornment, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Refresh as RecalcIcon,
} from '@mui/icons-material';
import { format, subDays, startOfMonth } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useDayOverviews } from '../hooks/useDayOverviews';
import { useStaff } from '../../staff/hooks/useStaff';
import { useRegistrationCodes } from '../hooks/useRegistrationCodes';
import {
  useTidsregistreringPlannedShiftPermissions,
  useTidsregistreringRegistrationCodePermissions,
} from '../../auth/hooks/usePermissions';
import { usePlannedShifts } from '../hooks/usePlannedShifts';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import StaffMember from '../../../components/common/StaffMember';
import DayOverviewApprovalDialog from '../components/day-overview/DayOverviewApprovalDialog';
import DayTimeline from '../components/day-overview/DayTimeline';
import { SEVERITY_CHIP } from '../constants/severity';

import PageHeader from '../../../components/common/PageHeader';
import HowToRegIcon from '@mui/icons-material/HowToReg';

function ApprovalIcon({ approved }) {
  return approved
    ? <CheckCircleIcon fontSize="small" color="success" />
    : <UncheckedIcon fontSize="small" color="disabled" />;
}

export default function DayOverviewApprovalsPage() {
  const { t } = useTranslation(['tidsregistering', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { dayOverviews, fetchForDepartment, approveByAdmin, updateTimeDetailCode, recalculateBalances, loading, error } = useDayOverviews();
  const { staff, fetchDepartmentStaff } = useStaff();
  const { registrationCodes, fetchForDepartment: fetchCodes } = useRegistrationCodes();
  const { plannedShifts, fetchForDepartment: fetchShifts } = usePlannedShifts();
  // The planned shift is shown next to each day when the user may see shifts
  const { canRead: canReadShifts } = useTidsregistreringPlannedShiftPermissions();

  const [startDate, setStartDate] = useState(startOfMonth(new Date()));
  const [endDate, setEndDate] = useState(new Date());
  const [filter, setFilter] = useState('all'); // 'all' | 'pending' | 'approved'
  const [search, setSearch] = useState('');
  const [selectedOverview, setSelectedOverview] = useState(null);
  const [approveLoading, setApproveLoading] = useState(false);
  const [reclassifyLoading, setReclassifyLoading] = useState(false);
  const [recalcLoading, setRecalcLoading] = useState(null); // day_overview_id being recalculated

  const staffMap = Object.fromEntries((staff ?? []).map((s) => [s.staff_member_id, s]));
  const shiftMap = new Map((plannedShifts ?? []).map((s) => [s.planned_shift_id, s]));

  const load = useCallback(() => {
    const start = format(startDate, 'yyyy-MM-dd');
    const end = format(endDate, 'yyyy-MM-dd');
    fetchForDepartment(start, end);
    if (canReadShifts) fetchShifts(start, end);
  }, [fetchForDepartment, fetchShifts, startDate, endDate, canReadShifts]);

  const { canRead: canReadCodes } = useTidsregistreringRegistrationCodePermissions();
  useEffect(() => { fetchDepartmentStaff(); if (canReadCodes) fetchCodes(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const filtered = dayOverviews
    .filter((o) => {
      if (filter === 'pending') return !o.approved_by_admin;
      if (filter === 'approved') return o.approved_by_admin;
      return true;
    })
    .filter((o) => {
      if (!search) return true;
      const member = staffMap[o.staff_member_id];
      return member?.name?.toLowerCase().includes(search.toLowerCase());
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const handleReclassify = useCallback(async (timeDetailId, registrationCodeId) => {
    setReclassifyLoading(true);
    const result = await updateTimeDetailCode(timeDetailId, { registration_code_id: registrationCodeId });
    setReclassifyLoading(false);
    if (result.success) {
      // Update selected overview's time details in place
      setSelectedOverview((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          time_details: prev.time_details.map((td) =>
            td.time_detail_id === timeDetailId
              ? { ...td, registration_code_id: registrationCodeId }
              : td
          ),
        };
      });
      load();
    } else {
      showError(result.error ?? 'Kunne ikke omklassificere');
    }
  }, [updateTimeDetailCode, showError, load]);

  const handleApprove = useCallback(async (dayOverviewId) => {
    setApproveLoading(true);
    const result = await approveByAdmin(dayOverviewId, { approver_info: '' });
    setApproveLoading(false);
    if (result.success) {
      showSuccess('Dag godkendt');
      setSelectedOverview((prev) => prev?.day_overview_id === dayOverviewId ? result.data : prev);
      load();
    } else {
      showError(result.error ?? 'Kunne ikke godkende');
    }
  }, [approveByAdmin, showSuccess, showError, load]);

  const handleRecalculate = useCallback(async (dayOverviewId) => {
    setRecalcLoading(dayOverviewId);
    const result = await recalculateBalances(dayOverviewId);
    setRecalcLoading(null);
    if (result.success) {
      showSuccess(result.data ? 'Saldo genberegnet' : 'Ingen merarbejde — ingen saldo at beregne');
    } else {
      showError(result.error ?? 'Kunne ikke genberegne saldo');
    }
  }, [recalculateBalances, showSuccess, showError]);

  const pendingCount = dayOverviews.filter((o) => !o.approved_by_admin).length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={HowToRegIcon}
        title={t('common:navigation.tidsregistering_approvals')}
        subtitle={t('tidsregistering:page_subtitles.approvals')}
        badges={pendingCount > 0 && (
          <Chip label={`${pendingCount} afventer`} color="warning" size="small" />
        )}
      />

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <>
          <DatePicker
            label="Fra"
            value={startDate}
            onChange={(d) => d && setStartDate(d)}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="Til"
            value={endDate}
            onChange={(d) => d && setEndDate(d)}
            slotProps={{ textField: { size: 'small' } }}
          />
        </>

        <TextField
          size="small"
          placeholder="Søg medarbejder…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }}
        />

        <ToggleButtonGroup
          size="small"
          value={filter}
          exclusive
          onChange={(_, v) => v && setFilter(v)}
        >
          <ToggleButton value="all">Alle</ToggleButton>
          <ToggleButton value="pending">Afventer</ToggleButton>
          <ToggleButton value="approved">Godkendt</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Medarbejder</TableCell>
              <TableCell>Dato</TableCell>
              <TableCell align="right">Timer</TableCell>
              <TableCell align="right">Afvigelse</TableCell>
              <TableCell align="center">Alvorlighed</TableCell>
              <TableCell align="center">Medarbejder</TableCell>
              <TableCell align="center">Admin</TableCell>
              <TableCell align="right">Handlinger</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && dayOverviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.disabled' }}>
                  Ingen dag-oversigter fundet
                </TableCell>
              </TableRow>
            ) : filtered.map((overview) => {
              const member = staffMap[overview.staff_member_id];
              const sev = SEVERITY_CHIP[overview.severity_code] ?? SEVERITY_CHIP[0];
              const diff = Number(overview.hours_diff_from_scheduled);
              const diffLabel = diff === 0 ? '–' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}t`;
              const rowOpacity = { opacity: overview.approved_by_admin ? 0.65 : 1 };

              return (
                <React.Fragment key={overview.day_overview_id}>
                  <TableRow
                    hover
                    sx={{ ...rowOpacity, '& .MuiTableCell-root': { borderBottom: 'none' } }}
                  >
                    <TableCell>
                      {member
                        ? <StaffMember staffMember={member} avatarSize={24} gap={1} nameVariant="body2" clickable={false} />
                        : <Typography variant="body2" color="text.disabled">{overview.staff_member_id.slice(0, 8)}…</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(overview.date), 'dd/MM/yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{Number(overview.hours_sum).toFixed(2)}t</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        color={diff > 0 ? 'warning.main' : diff < 0 ? 'error.main' : 'text.secondary'}
                      >
                        {diffLabel}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={sev.label} color={sev.color} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <ApprovalIcon approved={overview.approved_by_user} />
                    </TableCell>
                    <TableCell align="center">
                      <ApprovalIcon approved={overview.approved_by_admin} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Vis detaljer">
                          <IconButton size="small" onClick={() => setSelectedOverview(overview)}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {overview.approved_by_admin && (
                          <Tooltip title="Genberegn saldo (afspadsering)">
                            <IconButton
                              size="small"
                              color="info"
                              disabled={recalcLoading === overview.day_overview_id}
                              onClick={() => handleRecalculate(overview.day_overview_id)}
                            >
                              {recalcLoading === overview.day_overview_id
                                ? <CircularProgress size={14} />
                                : <RecalcIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        {!overview.approved_by_admin && (
                          <Tooltip title="Godkend som admin">
                            <IconButton
                              size="small"
                              color="success"
                              disabled={approveLoading}
                              onClick={() => handleApprove(overview.day_overview_id)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                  {/* Day timeline strip */}
                  <TableRow sx={rowOpacity}>
                    <TableCell colSpan={8} sx={{ pt: 0, pb: 0.75, px: 2 }}>
                      <DayTimeline
                        timeDetails={overview.time_details ?? []}
                        registrationCodes={registrationCodes}
                        plannedShifts={overview.planned_shift_id && shiftMap.has(overview.planned_shift_id)
                          ? [shiftMap.get(overview.planned_shift_id)]
                          : []}
                        height={16}
                      />
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail dialog */}
      <DayOverviewApprovalDialog
        open={!!selectedOverview}
        overview={selectedOverview}
        staffMember={selectedOverview ? staffMap[selectedOverview.staff_member_id] : null}
        registrationCodes={registrationCodes}
        plannedShift={selectedOverview?.planned_shift_id ? shiftMap.get(selectedOverview.planned_shift_id) ?? null : null}
        onClose={() => setSelectedOverview(null)}
        onApprove={handleApprove}
        onReclassify={handleReclassify}
        onRecalculate={handleRecalculate}
        approveLoading={approveLoading}
        reclassifyLoading={reclassifyLoading}
        recalcLoading={recalcLoading === selectedOverview?.day_overview_id}
      />
    </Box>
  );
}
